import { computed } from "vue";
import { getCartList } from "@/api/mall";
import { useSiteTemplate } from "@/composables/useSiteTemplate";
import config from "@/config/index";
import { parseSiteConfigData } from "@/utils/siteSeo";

export const LOCAL_CART_STORAGE_KEY = "local_cart_items";

export function normalizeCartMaxQuantity(value) {
  const quantity = Number(value);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return 0;
  }

  return Math.floor(quantity);
}

export function getCartItemQuantity(item) {
  const quantity = Number(
    item?.buyAmount
    ?? item?.comboBuyQty
    ?? item?.qty
    ?? item?.quantity
    ?? item?.count
    ?? 1,
  );
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
}

export function getCartTotalQuantity(items) {
  return (Array.isArray(items) ? items : []).reduce(
    (total, item) => total + getCartItemQuantity(item),
    0,
  );
}

function findMatchingCartItem(items, targetItem) {
  const normalizedItems = Array.isArray(items) ? items : [];
  if (!targetItem || typeof targetItem !== "object") {
    return null;
  }

  if (targetItem.cartLineKey) {
    const matchedLine = normalizedItems.find(
      item => item?.cartLineKey === targetItem.cartLineKey,
    );
    if (matchedLine) return matchedLine;
  }

  const targetSkuId = targetItem.dealerItemSkuId || targetItem.spItemSkuId;
  if (targetSkuId) {
    const matchedSku = normalizedItems.find((item) => {
      const itemSkuId = item?.dealerItemSkuId || item?.spItemSkuId;
      return String(itemSkuId) === String(targetSkuId)
        && String(item?.specsInfo || "") === String(targetItem.specsInfo || "");
    });
    if (matchedSku) return matchedSku;
  }

  if (targetItem.id !== undefined && targetItem.id !== null) {
    return normalizedItems.find(item => String(item?.id) === String(targetItem.id)) || null;
  }

  return null;
}

export function resolveCartMaxQuantity(...sources) {
  for (const source of sources) {
    const parsedSource = source?.configData !== undefined
      ? parseSiteConfigData(source.configData)
      : parseSiteConfigData(source);
    const maxQuantity = normalizeCartMaxQuantity(parsedSource.cartMaxQuantity);
    if (maxQuantity > 0) {
      return maxQuantity;
    }
  }

  return 0;
}

export function useCartLimit() {
  const { currentSiteInfo, templateContext } = useSiteTemplate();

  const maxCartQuantity = computed(() => resolveCartMaxQuantity(
    templateContext.value,
    currentSiteInfo.value,
  ));

  const isCartLimitEnabled = computed(() => maxCartQuantity.value > 0);

  async function loadCurrentCartItems() {
    if (!process.client) {
      return [];
    }

    const isLoggedIn = Boolean(window.localStorage.getItem(config.appTokenKey));
    if (!isLoggedIn) {
      try {
        const storedCart = window.localStorage.getItem(LOCAL_CART_STORAGE_KEY);
        const parsedCart = storedCart ? JSON.parse(storedCart) : [];
        return Array.isArray(parsedCart) ? parsedCart : [];
      }
      catch (error) {
        console.error("Failed to read cart while checking cart limit", error);
        return [];
      }
    }

    const response = await getCartList();
    const cartItems = response?.data?.dataList ?? response?.data ?? [];
    return Array.isArray(cartItems) ? cartItems : [];
  }

  async function checkCanAddQuantity(addQuantity = 1) {
    const normalizedAddQuantity = Math.max(0, Number(addQuantity) || 0);
    if (!isCartLimitEnabled.value || normalizedAddQuantity === 0) {
      return {
        allowed: true,
        currentQuantity: 0,
        maxQuantity: maxCartQuantity.value,
      };
    }

    try {
      const cartItems = await loadCurrentCartItems();
      const currentQuantity = getCartTotalQuantity(cartItems);
      return {
        allowed: currentQuantity + normalizedAddQuantity <= maxCartQuantity.value,
        currentQuantity,
        maxQuantity: maxCartQuantity.value,
      };
    }
    catch (error) {
      console.error("Failed to check cart limit", error);
      return {
        allowed: true,
        currentQuantity: 0,
        maxQuantity: maxCartQuantity.value,
      };
    }
  }

  async function checkCanChangeQuantity(targetItem, nextQuantity) {
    const normalizedNextQuantity = Math.max(0, Number(nextQuantity) || 0);
    if (!isCartLimitEnabled.value) {
      return {
        allowed: true,
        currentQuantity: 0,
        maxQuantity: maxCartQuantity.value,
      };
    }

    try {
      const cartItems = await loadCurrentCartItems();
      const currentQuantity = getCartTotalQuantity(cartItems);
      const matchedItem = findMatchingCartItem(cartItems, targetItem);
      const previousQuantity = matchedItem ? getCartItemQuantity(matchedItem) : 0;
      const nextTotalQuantity = currentQuantity - previousQuantity + normalizedNextQuantity;

      return {
        // 已有购物车超过新配置上限时，仍允许用户逐步减少数量。
        allowed: normalizedNextQuantity <= previousQuantity
          || nextTotalQuantity <= maxCartQuantity.value,
        currentQuantity,
        maxQuantity: maxCartQuantity.value,
      };
    }
    catch (error) {
      console.error("Failed to check cart quantity change", error);
      return {
        allowed: true,
        currentQuantity: 0,
        maxQuantity: maxCartQuantity.value,
      };
    }
  }

  function checkCartQuantity(items) {
    const currentQuantity = getCartTotalQuantity(items);
    return {
      allowed: !isCartLimitEnabled.value || currentQuantity <= maxCartQuantity.value,
      currentQuantity,
      maxQuantity: maxCartQuantity.value,
    };
  }

  return {
    maxCartQuantity,
    isCartLimitEnabled,
    checkCanAddQuantity,
    checkCanChangeQuantity,
    checkCartQuantity,
  };
}

export default useCartLimit;
